import {
  decimal,
  spot as spotCore,
  type AutopilotControlTone,
  type SpotCardView,
  type SpotView,
} from "@auto-pools/core";

/**
 * Pure presentation helpers for the spot card (design `50`-`52`, `56`). Every value is read
 * straight from `SpotView`; nothing here invents a figure the server does not already provide.
 * Color/tone always goes through `packages/core/src/spot/controls.ts spotTone`, so the web, the
 * pill and Telegram can never disagree on what counts as "money at risk".
 */

export interface SpotCardStatus {
  tone: AutopilotControlTone;
  /** The chip text, e.g. "Active · in coins", "Waiting", "Money at risk", "Not running · no key". */
  label: string;
}

/**
 * Where the key stands while the spot leg is off — the one predicate the header chip and the
 * subtitle both read, so they can never disagree. `hold` is null while off (the server sets it
 * only for an active run), so the vault and the key are read from `spot.key` itself; `usable` is
 * the server's own "may orders be signed now" gate.
 */
export type SpotOffKey = "vault-locked" | "ready" | "not-working" | "waiting-for-key" | "no-key";

export function spotOffKey(spot: SpotView): SpotOffKey {
  if (spot.key.vault === "locked" || spot.hold === "vault-locked") return "vault-locked";
  if (spot.key.active !== null) return spot.key.usable ? "ready" : "not-working";
  return spot.pendingKey ? "waiting-for-key" : "no-key";
}

const OFF_LABEL: Record<SpotOffKey, string> = {
  "vault-locked": "Not running · vault locked",
  ready: "Not running",
  "not-working": "Not running · key not working",
  "waiting-for-key": "Not running · waiting for a key",
  "no-key": "Not running · no key",
};

/** The meta line under the title while the spot leg is off (design 52's "not started" card). */
export function spotOffSubtitle(spot: SpotView): string {
  switch (spotOffKey(spot)) {
    case "ready":
      return spot.actions.start.allowed
        ? "The key is ready. Start spot and it buys with the next daily decision. Pools are unaffected."
        : "The key is ready. Spot starts together with the autopilot's Start.";
    case "not-working":
      return "The saved Bybit key is not working, so spot cannot trade. Check it in Settings. Pools are unaffected.";
    case "vault-locked":
      return "The vault is locked, so the Bybit key cannot sign orders until it is unlocked. Pools are unaffected.";
    case "waiting-for-key":
    case "no-key":
      return "Add a Bybit key on a separate subaccount and the spot engine starts with the next daily decision. Pools are unaffected.";
  }
}

/** The header chip (design 50/52): tone always from `spotTone`, text from the state/reason/hold. */
export function spotCardStatus(spot: SpotView): SpotCardStatus {
  const tone = spotCore.spotTone(spot);
  if (spot.state === "needs-attention") {
    const text =
      spot.reason === "sale-failing"
        ? "Money at risk"
        : spot.reason === "key-broken-holding"
          ? "Money at risk · key not working"
          : spot.reason === "books-mismatch"
            ? "Needs attention · books mismatch"
            : spot.reason === "engine-stalled"
              ? "Needs attention · engine errors"
              : "Needs attention · waiting for candles";
    return { tone, label: text };
  }
  if (spot.hold !== null && spot.lifecycle !== "off" && spot.lifecycle !== "paused")
    return {
      tone,
      label:
        spot.hold === "vault-locked"
          ? "Not trading · vault locked"
          : "Not trading · key not working",
    };
  switch (spot.lifecycle) {
    case "off":
      return { tone, label: OFF_LABEL[spotOffKey(spot)] };
    case "starting":
      return { tone, label: "Starting · reading the subaccount" };
    case "stopping":
      return { tone, label: "Stopping…" };
    case "paused":
      return { tone, label: "Paused" };
    case "running":
      return { tone, label: spot.running === "in-coins" ? "Active · in coins" : "Waiting" };
  }
}

/** The card title (design 50: "12 coins / USDT"; 52: "No coins · all in USDT" / "Spot · Bybit"). */
export function spotCardTitle(spot: SpotView): string {
  if (spot.lifecycle === "off") return "Spot · Bybit";
  if (spot.coins.length > 0) return `${String(spot.coins.length)} coins / USDT`;
  return "No coins · all in USDT";
}

/** The icon stack's slot count: every coin fits as its own avatar up to this many. */
const AVATAR_SLOTS = 5;

/**
 * Coin tickers for the icon stack. Up to `AVATAR_SLOTS` held coins each get their own avatar;
 * beyond that, the first four are shown and the rest counted as overflow — never a "+1" taking
 * the one slot a fifth coin would otherwise fill.
 */
export function spotAvatarCoins(spot: SpotView): { shown: string[]; overflow: number } {
  const tickers = spot.coins.map((coin) => coin.coin);
  if (tickers.length <= AVATAR_SLOTS) return { shown: tickers, overflow: 0 };
  return { shown: tickers.slice(0, 4), overflow: tickers.length - 4 };
}

export interface SpotAllocation {
  calmPercent: number;
  seasonPercent: number;
  usdtPercent: number;
}

/**
 * The Allocation bar's three shares (design 50/51's "calm 44 % · season 39 % · USDT 17 %"). Each
 * slice already states its own `half`, `cash` and `value` (cash plus holdings at the best bid), so
 * a half's holdings-only value is `value - cash` and free USDT is the sum of every slice's cash —
 * no coin-to-half inference, just addition of fields the server already returned. Null when any
 * slice needed for the sum has no value yet (a fresh run before its first snapshot).
 */
export function spotAllocation(spot: SpotView): SpotAllocation | null {
  if (spot.slices.length === 0) return null;
  let calmHoldings = decimal("0");
  let seasonHoldings = decimal("0");
  let cash = decimal("0");
  for (const slice of spot.slices) {
    if (slice.value === null) return null;
    const holdings = decimal(slice.value).sub(decimal(slice.cash));
    if (slice.half === "calm") calmHoldings = calmHoldings.add(holdings);
    else seasonHoldings = seasonHoldings.add(holdings);
    cash = cash.add(decimal(slice.cash));
  }
  const total = calmHoldings.add(seasonHoldings).add(cash);
  const totalNumber = Number(total.toString());
  if (!Number.isFinite(totalNumber) || totalNumber <= 0) return null;
  return {
    calmPercent: (Number(calmHoldings.toString()) / totalNumber) * 100,
    seasonPercent: (Number(seasonHoldings.toString()) / totalNumber) * 100,
    usdtPercent: (Number(cash.toString()) / totalNumber) * 100,
  };
}

/** Whether the card should show "Pause and sell → USDT" (the one spot button, design 50/52). */
export function spotShowsPauseButton(spot: SpotView): boolean {
  return spot.actions.pause.allowed;
}

/**
 * `amount / base` in percent, exactly (core `Decimal`, 8 places truncated). Null when either side
 * is unavailable or the base is not positive — a ratio of a non-positive base has no meaning, so
 * it is never shown as 0 % or ±∞.
 */
export function spotPercentOf(amount: string | null, base: string | null): string | null {
  if (amount === null || base === null) return null;
  const denominator = decimal(base);
  if (denominator.units <= 0n) return null;
  return decimal(amount).mul(decimal("100")).div(denominator, 8).toString();
}

/**
 * The coins alone at the best bid: the exact sum of every `coins[].value`. `card.value` also holds
 * the slices' cash, so it is not this number. Null while the subaccount is unread (no slices), when
 * nothing is held (an empty sum is not a balance), or when any coin has no value.
 */
export function spotCoinsValue(spot: SpotView): string | null {
  if (spot.slices.length === 0 || spot.coins.length === 0) return null;
  let total = decimal("0");
  for (const coin of spot.coins) {
    if (coin.value === null) return null;
    total = total.add(decimal(coin.value));
  }
  return total.toString();
}

/**
 * Trading cost paid so far: fees on fills plus slippage against each order's reference price
 * (positive = cost). The total exists only when both parts are known; the parts are itemized
 * either way, so a known fee is never hidden and a partial sum is never shown as the total.
 */
export function spotTradingCost(card: SpotCardView): {
  total: string | null;
  fees: string | null;
  slippage: string | null;
} {
  const { tradingFees: fees, slippage } = card;
  const total =
    fees === null || slippage === null ? null : decimal(fees).add(decimal(slippage)).toString();
  return { total, fees, slippage };
}

export interface SpotExitCell {
  label: "Auto-exit" | "Auto-entry";
  value: string;
  tone: "negative" | "";
  note: string;
}

/**
 * The seventh column (design 50/52). "Enabled" / "Armed" are promises that the engine sells (or
 * buys back) on its own, so they are shown only while it actually runs its decisions: lifecycle
 * `running`, no attention reason, no key/vault hold. Every other state says what is true instead.
 */
export function spotExitCell(spot: SpotView): SpotExitCell {
  const waiting = spot.running === "waiting";
  const label = waiting ? "Auto-entry" : "Auto-exit";
  if (spot.state === "needs-attention")
    switch (spot.reason) {
      case "sale-failing":
        return {
          label: "Auto-exit",
          value: "Failing",
          tone: "negative",
          note:
            spot.saleFailing === null
              ? "the sale keeps failing · the bot keeps trying and will not give up"
              : `${String(spot.saleFailing.attempts)} attempts · the bot keeps trying and will not give up`,
        };
      case "key-broken-holding":
        return {
          label: "Auto-exit",
          value: "Not working",
          tone: "negative",
          note: "the key stopped working · the coins cannot be sold until it is replaced",
        };
      case "books-mismatch":
        return {
          label,
          value: "Stopped",
          tone: "",
          note: "no orders until the books match the subaccount again",
        };
      case "engine-stalled":
        return {
          label,
          value: "Delayed",
          tone: "negative",
          note: "engine steps keep failing · may be delayed, retried every few seconds",
        };
      default:
        return {
          label,
          value: "Waiting",
          tone: "",
          note: "no decision until the daily candles can be read",
        };
    }
  const active = spot.lifecycle === "starting" || spot.lifecycle === "running";
  if (active && spot.hold !== null)
    return {
      label,
      value: "Not trading",
      tone: "",
      note:
        spot.hold === "vault-locked"
          ? "the vault is locked · nothing is bought or sold"
          : spot.hold === "no-key"
            ? "no key · nothing is bought or sold"
            : "the key is not working · nothing is bought or sold",
    };
  switch (spot.lifecycle) {
    case "running":
      return waiting
        ? {
            label,
            value: "Armed",
            tone: "",
            note: "buys back on the first daily close above the average · check 00:00 UTC",
          }
        : {
            label,
            value: "Enabled",
            tone: "",
            note: "all → USDT on a daily close below the average · next check 00:00 UTC",
          };
    case "starting":
      return {
        label,
        value: "Not yet",
        tone: "",
        note: spot.readingSubaccount
          ? "reading the subaccount first"
          : "first decision at the next 00:00 UTC close",
      };
    case "stopping":
      return spot.stopMode === "keep"
        ? { label, value: "Off", tone: "", note: "stopping · the coins stay as they are" }
        : { label, value: "Selling", tone: "", note: "everything is being sold into USDT" };
    case "paused":
      return {
        label,
        value: "Off",
        tone: "",
        note:
          spot.coins.length > 0
            ? "paused · nothing is sold automatically, the coins are yours to carry"
            : "paused · nothing is bought or sold",
      };
    case "off":
      return { label, value: "Off", tone: "", note: "spot is not running" };
  }
}

/** When the failing sale is tried next: its coin's standing-exit retry; null when none is due. */
export function spotNextSaleAttempt(spot: SpotView): string | null {
  const failing = spot.saleFailing;
  if (failing === null) return null;
  let next: string | null = null;
  for (const exit of spot.standingExits)
    if (
      exit.coin === failing.coin &&
      (next === null || Date.parse(exit.nextAttemptAt) < Date.parse(next))
    )
      next = exit.nextAttemptAt;
  return next;
}

export interface SpotHalfCoverage {
  held: number;
  of: number;
}

/** Slices holding `coin` (holdings or recorded dust), counted per half against that half's size. */
export function spotCoinCoverage(
  spot: SpotView,
  coin: string,
): { calm: SpotHalfCoverage; season: SpotHalfCoverage } {
  const count = (half: "calm" | "season"): SpotHalfCoverage => {
    const slices = spot.slices.filter((slice) => slice.half === half);
    return {
      held: slices.filter(
        (slice) =>
          slice.holdings.some((h) => h.coin === coin) || slice.dust.some((h) => h.coin === coin),
      ).length,
      of: slices.length,
    };
  };
  return { calm: count("calm"), season: count("season") };
}

/** "7 of 7" for one half, "calm 7 of 7 · season 3 of 7" when both hold the coin; "—" for none. */
export function spotCoverageText(coverage: { calm: SpotHalfCoverage; season: SpotHalfCoverage }): {
  half: string;
  slices: string;
} {
  const { calm, season } = coverage;
  const of = (c: SpotHalfCoverage) => `${String(c.held)} of ${String(c.of)}`;
  if (calm.held > 0 && season.held > 0)
    return { half: "calm · season", slices: `calm ${of(calm)} · season ${of(season)}` };
  if (calm.held > 0) return { half: "calm", slices: of(calm) };
  if (season.held > 0) return { half: "season", slices: of(season) };
  return { half: "—", slices: "—" };
}
